const enUS = {
  gate: {
    title: 'Please sign in to use the editor',
    hint: 'The sign-in window should open automatically; you can also open it from the account icon in the top-right.',
  },
  editorDraft: {
    dialogTitle: 'Local draft found',
    dialogHint:
      'A cached project from your last session is still on this device. Restore it to keep editing, or discard it to start from the current blank project. (Screen textures must be re-uploaded.)',
    restore: 'Restore draft',
    discard: 'Discard',
    timeHint: 'Saved at: {time}',
    restoredStub: 'Restored project from local draft.',
    persistFailedTitle: 'Local draft saving paused',
    persistFailedBody:
      'The browser storage quota may be full. Download your project as JSON from “Save project”, or clear site data and try again.',
  },
  nav: {
    home: 'Home',
    projects: 'Projects',
    undo: 'Undo',
    redo: 'Redo',
    accountEntry: 'Account',
    marketingHome: 'Home',
    marketingTemplates: 'Templates',
    marketingTutorials: 'Tutorials',
    marketingPricing: 'Pricing',
    marketingContact: 'Contact us',
    marketingConsult: 'Consult',
    lang: 'Switch language',
    mode: 'Switch workspace mode',
    modeDefault: 'Default mode',
    modeEdit: 'Edit mode',
  },
  leftRail: {
    deviceLibrary: 'Device library',
    mockupType: 'Mockup type',
    mockupCategory: 'Mockup category',
    searchDevices: 'Search devices',
    addSelected: 'Add selected device to canvas',
    add: 'Add',
    editTemplate: 'Edit template',
    categoryPhone: 'Phone',
    categoryTablet: 'Tablet',
    categoryLaptop: 'Laptop',
    categoryWatch: 'Watch',
    categoryOther: 'Other',
    tagImageVideo: 'Image / Video',
    tagText: 'Text',
  },
  floatingTools: {
    toolbarAria: 'Model transform tools',
    moveAria: 'Move current model',
    rotateAria: 'Rotate current model',
    scaleAria: 'Scale current model',
    moveTitle: 'Move',
    rotateTitle: 'Rotate',
    scaleTitle: 'Scale',
  },
  viewport: {
    ariaLabel: 'Three.js viewport',
  },
  brand: {
    logoAria: 'Animock',
  },
  timeline: {
    panelLabel: 'Animation timeline',
    addAnimation: 'Add animation',
    scaleControl: 'Timeline scale control',
    zoomOut: 'Zoom out timeline',
    zoomIn: 'Zoom in timeline',
    zoomSlider: 'Timeline scale: fit all at min; up to around 300 px per second at max',
    play: 'Play',
    pause: 'Pause',
    current: 'Current',
    seconds: 'seconds',
    time: 'Time',
    totalTimelineSeconds: 'Total timeline seconds',
    snap: 'Snap',
    snapHint: 'Clip drag snap in seconds; 0 disables',
    list: 'List',
    tracks: 'Timeline',
    layersAria: 'Animation layers',
    layerListAria: 'Animation layer list',
    empty: 'No models available',
    layerSelected: '{name}, selected',
    layerSelect: 'Select layer {name}',
    addAnimationLocked: 'Layer is locked. Cannot add animation clip.',
    addAnimationOutOfVisibility:
      'Playhead is outside the visibility window. Cannot add animation clip.',
    lockLayer: 'Lock layer',
    unlockLayer: 'Unlock layer',
    duplicateLayer: 'Duplicate layer',
  },
  timelineTracks: {
    groupLabel: 'Timeline tracks; use arrow keys to nudge playhead when focused',
    laneAria: 'Animation track for {name}',
    trimStart: 'Trim clip start',
    trimEnd: 'Trim clip end',
    visibilityStart: 'Adjust visibility window start',
    visibilityEnd: 'Adjust visibility window end',
    keyframeInClip: '{seconds}s in clip',
    emptyLane: 'Add a mockup to edit animation',
    playheadAria: 'Playhead; drag to scrub',
  },
  lightingDock: {
    panelAria: 'Lighting and environment',
    title: 'Environment light',
    presetsAria: 'Lighting environment presets',
    presetAria: 'Light: {label}',
    ambientStrength: 'Ambient strength',
    ambientValue: 'Ambient multiplier {value}',
  },
  inspectorPanel: {
    inspectorAria: 'Inspector',
    tabsAria: 'Inspector tabs',
    tabBasic: 'Basic info',
    tabControls: 'Mockup controls',
  },
  inspectorScene: {
    title: 'Scene settings',
    description:
      'Width and height above represent the preview canvas. Resolution presets and ratio presets are independent single-choice groups.',
    previewDimsAria: 'Preview canvas dimensions',
    width: 'Width',
    height: 'Height',
    resolutionPresets: 'Resolution presets',
    resolutionHint:
      'Only affects exported image/video pixels and does not change ratio selection. Current export is about {width}×{height}.',
    resolution4kLifetimeOnly: '4K resolution is available to lifetime members only.',
    resolutionGroupAria: 'Resolution presets',
    ratioPresets: 'Ratio presets',
    ratioHint:
      'Defines preview canvas ratio and logical pixels. Export size is determined by current resolution and ratio together.',
    ratioGroupAria: 'Aspect ratio presets',
  },
  inspectorBackground: {
    title: 'Background color',
    type: 'Type',
    modeColor: 'Color',
    modeGradient: 'Gradient color',
    modeTransparent: 'Transparent',
    modeImage: 'Image',
    hexColor: 'Hex color',
    pickBackgroundColor: 'Pick background color',
    colorPresets: 'Color presets',
    solidPresetAria: 'Solid preset {value}',
    transparentHint: 'Transparent background uses checkerboard in the viewport.',
    imageHint: 'Upload 1 image as background (auto-centered and covers the viewport).',
    imagePick: 'Pick image',
    imageReplace: 'Replace image',
    imageClear: 'Clear',
    imageInvalidType: 'Only image files are supported (PNG / JPG / WEBP, etc.).',
  },
  inspectorGradient: {
    templatesAria: 'Gradient templates',
    fallbackTemplateName: 'Gradient {id}',
    customGradient: 'Custom gradient',
    colorStopsAria: 'Gradient color stops',
    stopColorAria: 'Color for stop {position}%',
    stopHexAria: 'Hex for stop at {position}%',
    stopPositionAria: 'Position for {hex}',
    removeStopAria: 'Remove stop {hex}',
    addColor: 'Add color',
  },
  inspectorExport: {
    title: 'Export settings',
    frameRate: 'Frame rate',
    exportFormat: 'Export format',
    operation: 'Export operation',
    exportImage: 'Export image',
    exportVideo: 'Export video',
    cancelExport: 'Cancel export',
    videoMp4TransparentBlocked:
      'Transparent backgrounds cannot be exported as MP4 (H.264 has no alpha). Switch the format to WebM to export.',
    mp4UnsupportedTitle: '2K MP4 export unavailable',
    mp4UnsupportedBody:
      'The current browser, system, or GPU environment cannot complete H.264 MP4 encoding at this resolution.\n\nTry one of these options:\n1. Export 1K as MP4.\n2. Keep 2K and switch to WebM.\n3. Retry in the latest Chrome / Edge.',
    vp9AlphaLayerTitle: 'Transparent video export unavailable',
    vp9AlphaLayerBody:
      'This browser does not support WebM VP9 with alpha. Turn off transparent background, or try Chrome / Edge and export again.',
    layerToastDismissAria: 'Dismiss',
    loginRequired: 'Please sign in to export.',
    preflightInProgress: 'Registering export…',
    preflightFailed: 'Export registration failed: {reason}',
    preflightToastTitle: 'Export registration failed',
    preflightToastBody:
      '{reason}\n\nOpen Mockup / Layers: make sure each device layer’s visibility window covers the export time, the layer is not locked, and the device template is valid, then try again.',
  },
  inspectorProject: {
    title: 'Project operation',
    save: 'Save project',
    load: 'Load project',
    reset: 'Reset project',
  },
  inspector: {
    controls: {
      panelLabel: 'Mockup controls',
      selectionInfo: 'Selection info',
      operations: 'Mockup operations',
      pleaseSelectFirst: 'Please select a mockup first',
      layerName: 'Layer name',
      device: 'Device',
      deviceTemplateAria: 'Device template for this layer',
      position: 'Position',
      rotation: 'Rotation (deg)',
      scale: 'Scale',
      axisPosition: 'Position {axis}',
      axisRotate: 'Rotate {axis}',
      axisScale: 'Scale {axis}',
      animationClip: 'Animation clip',
      clipEasing: 'Clip easing',
      clipEasingAria: 'Clip easing preset (first frame to last frame)',
      clipHint: 'Each clip only has start and end keyframes. Move playhead to clip edge or blank area before editing transforms.',
      keyframesAria: 'Clip endpoints',
      firstFrame: 'First frame',
      lastFrame: 'Last frame',
      materialName: 'Material name',
      noMaterial: 'No screen material set',
      selectMaterialFile: 'Select material file',
      videoTextureAria: 'Video screen texture',
      videoFollowsTimeline: 'Video follows timeline automatically.',
      timeOffsetSec: 'Time offset (s)',
      resetPosition: 'Reset position',
      removeAnimation: 'Remove animation',
      removeMockup: 'Remove mockup',
      copyKeyframePose: 'Copy keyframe',
      pasteKeyframePose: 'Paste keyframe',
      easingLinear: 'Linear',
      easingEaseIn: 'Ease in',
      easingEaseOut: 'Ease out',
      easingEaseInOut: 'Ease in out',
      easingBounceOut: 'Bounce out',
      easingBackOut: 'Back out',
      lockHint: 'This layer is locked: delete/duplicate is allowed, but editing properties and timeline content is disabled.',
      imagePlaneUpload: 'Select image / video file',
      imagePlaneHint: 'After uploading, the plane will auto-resize to match the original aspect ratio.',
      textContent: 'Text content',
      textFontSize: 'Font size (px)',
      textFontWeight: 'Font weight',
      textFontWeightNormal: 'Normal',
      textFontWeightBold: 'Bold',
      textAlign: 'Text align',
      textAlignLeft: 'Left',
      textAlignCenter: 'Center',
      textAlignRight: 'Right',
      textColor: 'Text color',
      textPlaneHint: 'Uses the system default font, supports line breaks, and does not support custom fonts.',
      materialOpacityLabel: 'Material opacity',
      materialOpacitySliderAria: 'Material opacity for the selected keyframe; 0 is fully transparent, 1 is opaque',
      materialOpacityValue: 'Current: {value}',
      screenImageStackHint:
        'Upload multiple static images (max {max}, max edge {edge}px) and bind them per timeline keyframe.',
      screenImageListAria: 'Screen image list',
      screenImageRemove: 'Remove',
      keyframeScreenImage: 'Screen image at this keyframe',
      keyframeScreenImageInherit: 'Inherit (first image)',
      overwriteVideoWithImagesTitle: 'Switch to multiple images?',
      overwriteVideoWithImagesBody:
        'This removes the current video and uses the selected still images. You can bind different images on keyframes afterward.',
      overwriteImagesWithVideoTitle: 'Switch to video?',
      overwriteImagesWithVideoBody: 'This clears all screen images and replaces them with the selected video.',
      screenMediaOverwriteConfirm: 'Replace',
      screenMediaOverwriteCancel: 'Cancel',
      screenImageOversize: 'Image width and height must each be ≤ {max}px (this file is {w}×{h}).',
      screenImageGifRejected: 'GIF is not supported. Use PNG, JPEG, or WebP.',
      screenImageLoadFailed: 'Could not read this image.',
      screenImageMaxCount: 'At most {max} screen images; you can add {room} more.',
      screenRasterTransition: 'Multi-image transition',
      screenRasterTransitionAria:
        'GPU transition when switching stacked images on the timeline (session preference; applies from the next switch)',
      screenRasterTransitionNone: 'None (instant)',
      screenRasterTransitionFade: 'Cross-fade',
      screenRasterTransitionSlideLeft: 'Slide left',
      screenRasterTransitionSlideRight: 'Slide right',
      screenRasterTransitionSlideUp: 'Slide up',
      screenRasterTransitionSlideDown: 'Slide down',
      screenRasterTransitionZoom: 'Zoom',
      screenRasterTransitionCircle: 'Circle reveal',
    },
  },
  auth: {
    loginTab: 'Login',
    registerTab: 'Sign up',
    loginTitle: 'Email login',
    registerTitle: 'Email sign up',
    forgotTitle: 'Reset password',
    tabsAria: 'Switch auth mode',
    close: 'Close auth dialog',
    email: 'Email',
    code: 'Code',
    password: 'Password',
    newPassword: 'New password',
    sendCode: 'Send code',
    submitting: 'Submitting...',
    submitLogin: 'Login',
    submitRegister: 'Sign up',
    submitForgot: 'Reset password',
    emailInvalid: 'Enter a valid email address',
    emailMissingForSendCode: 'Please enter your email',
    codeSent: 'Code sent. Please check your inbox',
    loginSuccess: 'Login success',
    registerSuccess: 'Sign up success, please login',
    noAccount: 'No account yet?',
    goRegister: 'Create account',
    requestFailed: 'Request failed. Please try again',
    sendCodeRateLimited: 'Too many requests. Please try again later',
    invalidCode: 'Invalid or expired verification code',
    emailRegistered: 'This email is already registered',
    userNotFound: 'Account not found. Please sign up first',
    passwordInvalid: 'Incorrect password',
    passwordTooShort: 'Password must be at least 6 characters',
    forgotPassword: 'Forgot password',
    backToLoginPrefix: 'Remembered password?',
    backToLogin: 'Back to login',
    resetCodeSent: 'Reset code sent. Please check your inbox',
    resetSuccess: 'Password reset success, please login',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    passwordRuleHint: 'Use 8+ chars with upper/lowercase, number and symbol.',
    strengthWeak: 'Weak',
    strengthMedium: 'Medium',
    strengthStrong: 'Strong',
    forgotStepHint: 'Enter email, request code, then set a new password.',
    testFillRequired: 'Fill required fields (test mode skips real validation)',
    captchaRequired: 'Please complete the human verification',
    captchaCancelled: 'Human verification cancelled',
    cooldownSeconds: '__N__s',
    rememberMe: 'Remember me',
    rememberDaysLabel: 'Stay signed in',
    rememberDaysTpl: '__DAYS__ days',
    googleDivider: 'Or continue with',
    githubButton: 'Continue with GitHub',
    wechatButton: 'Continue with WeChat',
    wechatPopupTitle: 'WeChat Login',
    captcha: {
      title: 'Human verification',
      tipPrefix: 'Click in order:',
      refresh: 'Refresh',
      undo: 'Undo',
      confirm: 'Confirm',
      close: 'Close captcha dialog',
      loading: 'Loading...',
      stageAlt: 'Click the highlighted targets in order',
      loadError: 'Load failed, please refresh',
    },
    profile: {
      title: 'Account',
      close: 'Close account dialog',
      emailLabel: 'Email',
      subscriptionLabel: 'Subscription',
      subscribed: 'Active',
      notSubscribed: 'Not subscribed',
      subscribeNow: 'Subscribe',
      details: 'Details',
      detailsTitle: 'Subscription details',
      subscriptionSectionTitle: 'Subscription status',
      ordersSectionTitle: 'Orders',
      currentTier: 'Current plan',
      expiresLabel: 'Expires',
      expiresAt: 'Expires: {date}',
      permanent: 'Lifetime',
      currentPeriod: 'Current period',
      nextBilling: 'Next charge',
      renewal: 'Renewal',
      cancelAtPeriodEnd: 'Cancel at period end',
      refresh: 'Refresh',
      loading: 'Loading...',
      totalTpl: '{n} total',
      emptyOrders: 'No orders yet',
      prevPage: 'Prev',
      nextPage: 'Next',
      pageTpl: 'Page {p}',
      pagerAria: 'Orders pagination',
      orderNoLabel: 'Order #',
      payChannelLabel: 'Channel',
      payStatusLabel: 'Status',
      payTimeLabel: 'Paid at',
      productLabel: 'Product',
      unknown: '—',
      orderPayStatus: {
        pending: 'Pending',
        pe: 'Paid',
        paid: 'Paid',
        closed: 'Closed',
        refunded: 'Refunded',
      },
      orderType: {
        one_time: 'One-time purchase',
        subscription: 'Subscription purchase',
      },
      cancelSubscription: 'Cancel subscription',
      cancelling: 'Processing...',
      cancelConfirmTitle: 'Cancel subscription?',
      cancelConfirmBody:
        'After confirming, auto-renew will be turned off. Your benefits remain active until the end of the current period.',
      confirm: 'Confirm',
      cancel: 'Close',
      cancelSuccess: 'Subscription updated',
      queryUnavailable: 'Subscription details unavailable',
      proxyNonJson:
        'The response is not JSON (likely an HTML error page). Ensure the editor targets the marketing site Nitro /api proxy: set VITE_MARKETING_ORIGIN to the marketing origin in production.',
      proxyHtml404:
        '404 with an HTML body usually means the request did not reach Nuxt (e.g. static nginx only). Configure VITE_MARKETING_ORIGIN and route /api to Node/Nitro.',
      proxyHttpError: 'Request failed (HTTP {status})',
      tier: {
        none: 'No subscription',
        monthly: 'Monthly',
        yearly: 'Yearly',
        lifetime: 'Lifetime',
      },
      subStatus: {
        pending: 'Pending',
        trialing: 'Trial',
        active: 'Active',
        past_due: 'Past due',
        paused: 'Paused',
        cancelled: 'Cancelled',
        expired: 'Expired',
        unknown:
          'Cannot reach the subscription service right now. Membership remains active; please refresh to retry.',
      },
      createdLabel: 'Member since',
      createdUnknown: '—',
      logout: 'Log out',
      avatarAria: 'Avatar: first letter {initial} for {email}',
    },
  },
  editorActions: {
    rename: 'rename',
    switchTemplate: 'switch device template',
    editPosition: 'edit position',
    editRotation: 'edit rotation',
    editScale: 'edit scale',
    editScreenMaterial: 'edit screen material',
    resetTransform: 'reset transform',
    removeClip: 'remove animation clip',
    addClip: 'add animation clip',
    pasteKeyframePose: 'paste keyframe pose',
    editClipEasing: 'edit clip easing',
    editTimelineClip: 'edit timeline clip',
    useViewportTransformTool: 'use viewport transform tool',
    editTextContent: 'edit text content',
    editTextFontSize: 'edit text font size',
    editTextColor: 'edit text color',
    editTextFontWeight: 'edit text font weight',
    editTextAlign: 'edit text alignment',
    editKeyframeMaterialOpacity: 'adjust keyframe material opacity',
    editKeyframeScreenImage: 'bind keyframe screen image',
  },
  editorMessages: {
    lockedCannot: 'Layer is locked. Cannot {action}.',
    playheadOutsideVisibilityWindow: 'Playhead is outside the visibility window. Cannot add an animation clip.',
    visibilityWindowTooShortForClip: 'Visibility window is shorter than 1 second. Cannot add an animation clip.',
    noGapInsideVisibilityWindow: 'No enough gap inside the visibility window to add a clip (minimum 1 second).',
    viewportDropNoLayerHit: 'Drop the file onto a model surface to replace the screen texture.',
    viewportDropInvalidFile: 'No supported image, HDR, or MP4 file (same rules as the inspector file picker).',
    viewportDropTextPlaneNotSupported: 'Text layers do not support dropping screen textures; edit text in the inspector.',
    screenVideoWebCodecUnsupported:
      'This video cannot be used as a screen texture: the browser cannot decode its video codec (e.g. MPEG-4 Part 2). Re-export as MP4 with an H.264 (AVC) video track and AAC audio, then upload again.',
  },
} as const

export default enUS
